import type { CardHooks } from "./registry";
import { makeBuff } from "../playtestEngine";

/**
 * Sharpening Mill — Item utility (in-play).
 *
 * Phase 3 rewrite:
 *   - onUtilityPlay: does NOTHING beyond placing the card in utilityInPlay
 *     as an Item (the engine handles that automatically when utilityType==="Item").
 *   - onActivate: called when the player clicks the "Activate" button on the
 *     in-play Item. Queues a buff so the NEXT attack gains the Piercing keyword.
 *     Once-per-round: gated by `onceFlagsThisRound["sharpeningMill-activate-${instanceId}"]`.
 *
 * The engine fires `onActivate` for in-play items via `activateSharpeningMill`
 * (see PlaytestBoard / CardActionPopup) — it checks the once-flag before
 * calling through. The hook itself also enforces the flag as a safety guard.
 *
 * Data-driven: `gain-piercing` effect lives in the buff so future cards can
 * grant the same keyword without touching engine code.
 */
export const sharpeningMill: CardHooks = {
  onUtilityPlay: (_ctx) => ({
    // Intentionally empty — the card goes to utilityInPlay as an Item.
    // The Activate button appears in the ItemPanel / CardActionPopup.
    log: ["Sharpening Mill enters play. Activate once per round to grant your next attack Piercing."],
  }),

  onActivate: (ctx) => {
    const flagKey = `sharpeningMill-activate-${ctx.instanceId ?? "unknown"}`;
    if (ctx.self.onceFlagsThisRound?.[flagKey]) {
      return { log: ["Sharpening Mill — already activated this round."] };
    }
    return {
      queueSelfBuff: makeBuff(
        "Sharpening Mill",
        "next-attack",
        { kind: "gain-piercing" },
        ctx.game.round,
      ),
      selfPatch: {
        onceFlagsThisRound: {
          ...(ctx.self.onceFlagsThisRound ?? {}),
          [flagKey]: true,
        },
      },
      log: ["Sharpening Mill activated — next attack gains Piercing."],
    };
  },
};
